/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductDeliveryOptions, SalesOrder, SimpleProduct, TransactionType } from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    getSalesOrderDetailsBySalesIdAsync,
    getSalesOrderDetailsByTransactionIdAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';
import {
    ArrayExtensions,
    buildCacheKey,
    getSimpleProducts,
    ObjectExtensions,
    ProductInput,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Sales order response interface.
 */
interface ISalesOrderWithHydrations {
    salesOrder: SalesOrder;
    products: SimpleProduct[];
    productDeliveryOptions?: ProductDeliveryOptions[];
}

/**
 * OrderTypes Types of orders.
 */
export enum orderTypes {
    salesOrder = 'salesOrder',
    transaction = 'transaction'
}

/**
 * Calls the Retail API and returns the sales order.
 * @param orderType - OrderType.
 * @param orderId - Unique OrderId for each order.
 * @returns Sales Order.
 */
const getSalesOrder = (orderType: string = '', orderId: string = '') => async (
    context: IActionContext
): Promise<SalesOrder | undefined> => {
    //  Local (1) searches the retail server database, and remote (2) searches
    // on the headquarters side. All (3) and none (0) are not supported.
    const localSearch = 1;
    return orderType === orderTypes.salesOrder
        ? getSalesOrderDetailsBySalesIdAsync({ callerContext: context }, orderId)
        : getSalesOrderDetailsByTransactionIdAsync({ callerContext: context }, orderId, localSearch);
};

/**
 * Calls the Retail API and returns the products.
 * @param productIds - The product ids of the product to retrieve.
 * @param channelId - The channel id for the products.
 * @returns Simple product data.
 */
const getProducts = (productIds: (string | number)[] = [], channelId?: number) => async (
    context: IActionContext
): Promise<SimpleProduct[]> => {
    const productInputs = productIds.map(
        productId => new ProductInput(productId, context.requestContext.apiSettings, channelId, undefined, context.requestContext)
    );
    return getSimpleProducts(productInputs, context);
};

/**
 * Action input.
 */
export class GetSalesOrderConfirmationWithHydrationsInput implements IActionInput {
    public orderType: string;

    public orderId: string;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(orderType: string, orderId: string, apiSettings: ICommerceApiSettings) {
        this.orderType = orderType;
        this.orderId = orderId;
        this.apiSettings = apiSettings;
    }

    /**
     * Get cache key.
     * @returns String.
     */
    public getCacheKey = () => buildCacheKey('SalesOrderConfirmationWithHydrations', this.apiSettings);

    /**
     * Get cache object.
     * @returns String.
     */
    public getCacheObjectType = () => `SalesOrderConfirmationWithHydrations-${this.orderType}-${this.orderId}`;

    /**
     * Get cache data type.
     * @returns String.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Actioncontext data.
 * @returns Sales Order Input.
 */
const createSalesOrderConfrimationWithHydrationsInput = (inputData: ICreateActionContext) => {
    const { salesId = '', transactionId = '' } = inputData.requestContext.query || {};
    if (salesId) {
        return new GetSalesOrderConfirmationWithHydrationsInput(orderTypes.salesOrder, salesId, inputData.requestContext.apiSettings);
    } else if (transactionId) {
        return new GetSalesOrderConfirmationWithHydrationsInput(
            orderTypes.transaction,
            transactionId,
            inputData.requestContext.apiSettings
        );
    }
    throw new Error('createSalesOrderConfrimationWithHydrationsInput - No salesId or transactionId provided.');
};

/**
 * Get sales order with hydrations action.
 * @param input - Input for data action.
 * @param context - Action context.
 * @returns Sales order response.
 */
export async function getSalesOrderConfirmationWithHydrationsAction(
    input: GetSalesOrderConfirmationWithHydrationsInput,
    context: IActionContext
): Promise<ISalesOrderWithHydrations> {
    if (!context) {
        throw new Error('getSalesOrderConfirmationWithHydrationsAction - Action context cannot be null/undefined');
    }

    const { orderType, orderId } = input;

    if (!orderType || !orderId) {
        context.trace('[getSalesOrderConfirmationWithHydrationsAction] No orderType or orderId provided.');
        return <ISalesOrderWithHydrations>{};
    }

    const salesOrder = await getSalesOrder(orderType, orderId)(context);

    if (!salesOrder) {
        context.trace('[getSalesOrderWithHydrationsAction] No salesOrder found.');
        return <ISalesOrderWithHydrations>{};
    }

    // If it is salesInvoice, return salesOrder and empty products
    if (salesOrder.TransactionTypeValue === TransactionType.SalesInvoice) {
        return {
            salesOrder,
            products: []
        };
    }

    if (!ArrayExtensions.hasElements(salesOrder.SalesLines)) {
        context.trace('[getSalesOrderWithHydrationsAction] No salesLine found.');
        return <ISalesOrderWithHydrations>{};
    }

    const productIds = salesOrder.SalesLines.map(salesLine => salesLine.ProductId ?? '');

    if (!ArrayExtensions.hasElements(productIds)) {
        context.trace('[getSalesOrderWithHydrationsAction] No products in saleLines found.');
        return <ISalesOrderWithHydrations>{};
    }

    const products = await getProducts(productIds, salesOrder.ChannelId)(context);

    if (!ArrayExtensions.hasElements(products)) {
        context.trace('[getSalesOrderWithHydrationsAction] No simple products found.');
        return <ISalesOrderWithHydrations>{};
    }

    const validProducts = products.filter(product => !ObjectExtensions.isNullOrUndefined(product.RecordId));
    const filterPickup = 4;
    const deliveryOptions = await getDeliveryOptionsAsync(
        { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
        validProducts.map(product => product.RecordId),
        {},
        filterPickup
    )
        .then(result => {
            return result;
        })
        .catch((error: Error) => {
            context.trace(error.message);
            context.telemetry.exception(error);
            context.telemetry.debug('[getDeliveryOptionsForCartLineItems]Error executing action');
            throw new Error('[getDeliveryOptionsForCartLineItems]Error executing action');
        });

    return <ISalesOrderWithHydrations>{
        salesOrder,
        products,
        deliveryOptions
    };
}

/**
 * Get sales order with hydrations action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Sales order response.
 */
export const getSalesOrderConfirmationWithHydrationsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-sales-order-confirmation-with-hydrations',
    action: <IAction<ISalesOrderWithHydrations>>getSalesOrderConfirmationWithHydrationsAction,
    input: createSalesOrderConfrimationWithHydrationsInput
});

export default getSalesOrderConfirmationWithHydrationsActionDataAction;
